// @ts-nocheck
/**
 * Shared list value helpers for item-variable and category-property `list` types.
 * Display/parse rules match EditEntry ListEdit and layout listView row labels.
 */

import { logger } from '../../../../../shims/appStub';
import { ITEM_VARIABLE_POLICY } from './itemVariablePolicy';

/**
 * Shallow-copy a list for persistence. Elements must be JSON-serializable
 * (checked via JSON.stringify). Nested arrays/objects are kept as-is.
 * Returns null when value is not an array, exceeds max elements, or is not serializable.
 */
export function coerceListValue(value: unknown): unknown[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  if (value.length > ITEM_VARIABLE_POLICY.maxListElements) {
    logger.warn(
      'ListValueUtils',
      `List exceeds max of ${ITEM_VARIABLE_POLICY.maxListElements} elements.`,
      { length: value.length, cap: ITEM_VARIABLE_POLICY.maxListElements },
    );
    return null;
  }
  try {
    return JSON.parse(JSON.stringify(value)) as unknown[];
  } catch {
    return null;
  }
}

/** Format a list entry for EditEntry / listView display. */
export function formatListEntryForDisplay(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (value === null || value === undefined) {
    return '';
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * Parse EditEntry text back into a list element.
 * true/false → boolean; finite number → number; `{`/`[` → JSON when valid; else string.
 */
export function parseListEntryFromText(text: string): unknown {
  const trimmed = text.trim();
  if (trimmed === 'true') {
    return true;
  }
  if (trimmed === 'false') {
    return false;
  }
  if (trimmed !== '' && /^-?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/.test(trimmed)) {
    const num = Number(trimmed);
    if (Number.isFinite(num)) {
      return num;
    }
  }
  if (
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'))
  ) {
    try {
      return JSON.parse(trimmed) as unknown;
    } catch {
      // fall through to string
    }
  }
  return text;
}

/** Normalize a typed list for category storage (null/undefined → []). */
export function normalizeListForStorage(value: unknown): unknown[] | null {
  if (value === null || value === undefined) {
    return [];
  }
  return coerceListValue(value);
}
